import {
  createAsyncThunk,
} from '@reduxjs/toolkit';

import type { ThunkApiType } from '@/store';
import type { NoticeItemJson } from '@/types/domain/notice/entities/noticeItemEntity';
import type { CategoryItemsType } from '@/types/domain/notice/redux';
import type { ContentAttachmentJson } from '@/types/domain/notice/valueObjects/contentAttachment';

import { noticeSlice } from '@/domain/notice/redux';
import { uiSlice } from '@/domain/ui/redux';
import { ApiResponseError } from '@/errors/apiResponseError';
import { service } from '@/share';

const genDateString = () => {
  const date = new Date();
  let format = 'yyyymmddHHmmssSSS';
  format = format.replace(/yyyy/g, date.getFullYear().toString());
  format = format.replace(/MM/g, `0${date.getMonth() + 1}`.slice(-2));
  format = format.replace(/dd/g, `0${date.getDate()}`.slice(-2));
  format = format.replace(/HH/g, `0${date.getHours()}`.slice(-2));
  format = format.replace(/mm/g, `0${date.getMinutes()}`.slice(-2));
  format = format.replace(/ss/g, `0${date.getSeconds()}`.slice(-2));
  format = format.replace(/SSS/g, `00${date.getMilliseconds()}`.slice(-3));
  return format;
};

/**
 * ダウンロードする
 * @param attachment attachment
 */
export const downloadAction = (attachment: ContentAttachmentJson) => {
  const filename = `${genDateString()}_${attachment.name}`;
  const link = document.createElement('a');
  link.href = attachment.content.replace('data:application/pdf', 'data:application/octet-stream');
  link.download = filename;
  link.click();
};

/**
 * カテゴリとページデータをリフレッシュする
 */
export const refreshCategoryItems = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
  },
  ThunkApiType
>(
  'notice/refreshCategoryItems',
  async ({ nurseryId }, thunkApi) => {
    const result: CategoryItemsType = {};
    const catResult = await service.notice.getCategoryList(nurseryId);
    for (let i = 0; i < catResult.items.length; i += 1) {
      const { categoryId } = catResult.items[i];
      const pageResult = await service.notice.getNoticeItemList(nurseryId, categoryId, null);
      result[categoryId] = {
        category: catResult.items[i],
        items: pageResult.items,
        nextToken: pageResult.nextToken,
      };
    }
    thunkApi.dispatch(noticeSlice.actions.setCategoryItems(result));
  },
);

/**
 * ページデータを取得
 */
export const getCategoryItems = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
    /** カテゴリID */
    categoryId: string,
  },
  ThunkApiType
>(
  'notice/getCategoryItems',
  async ({ nurseryId, categoryId }, thunkApi) => {
    const { categoryItems } = thunkApi.getState().notice;
    if (categoryItems && categoryItems[categoryId]) {
      const { nextToken } = categoryItems[categoryId];
      const pageResult = await service.notice.getNoticeItemList(
        nurseryId,
        categoryId,
        nextToken,
      );
      const result: CategoryItemsType = {};
      const catIdList = Object.keys(categoryItems);
      for (let i = 0; i < catIdList.length; i += 1) {
        const catId = catIdList[i];
        const categoryItem = categoryItems[catId];
        result[catId] = {
          category: { ...categoryItem.category },
          items: categoryItem.items.map((v) => ({ ...v })),
          nextToken: categoryItem.nextToken,
        };
        if (catId === categoryId) {
          pageResult.items.forEach((v) => {
            result[catId].items.push(v);
          });
          result[catId].nextToken = pageResult.nextToken;
        }
      }
      thunkApi.dispatch(noticeSlice.actions.setCategoryItems(result));
    }
  },
);

export const getAttachment = createAsyncThunk<
  void,
  {
    /** アイテム */
    item: NoticeItemJson,
    /** 施設ID */
    nurseryId: string,
  } | undefined,
  ThunkApiType
>(
  'notice/getAttachment',
  async (input, thunkApi) => {
    if (!input) {
      thunkApi.dispatch(noticeSlice.actions.setNoticeItemContent(undefined));
      return;
    }
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      const content = await service.notice.loadContentNotice(input.item, input.nurseryId);
      thunkApi.dispatch(noticeSlice.actions.setNoticeItemContent({ item: content }));
    } catch (e) {
      if (e instanceof ApiResponseError) {
        thunkApi.dispatch(uiSlice.actions.setMessage({
          key: 'error',
          content: 'データの取得に失敗しました',
        }));
      } else {
        throw e;
      }
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

export const downloadAttachment = createAsyncThunk<
  void,
  {
    /** アイテム */
    item: NoticeItemJson,
    /** 施設ID */
    nurseryId: string,
  },
  ThunkApiType
>(
  'notice/downloadAttachment',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      const content = await service.notice.loadContentNotice(input.item, input.nurseryId);
      downloadAction(content.attachment);
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: 'ダウンロードフォルダをご確認ください' }));
    } catch (e) {
      if (e instanceof ApiResponseError) {
        thunkApi.dispatch(uiSlice.actions.setMessage({
          key: 'error',
          content: 'データの取得に失敗しました',
        }));
      } else {
        throw e;
      }
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);
